import dayjs from "dayjs";
import type { StateCreator } from "zustand";

import { getLocalStorageOpportunityView } from "../client-helper";
import {
  defaultOpportunityResponseQuery,
  NO_STAGE_ID,
  OPPORTUNITY_VIEW,
} from "../constants/common";
import type { Opportunity } from "../types/common";
import type { ZOpportunityState } from "../types/zustand";
import { setActivePipelineInCookie } from "../utils/pipeline-cookie";

export const opportunitiesSlice: StateCreator<ZOpportunityState> = (
  set,
  get,
) => ({
  opportunityView: getLocalStorageOpportunityView() || OPPORTUNITY_VIEW.TABLE,
  setOpportunityView: opportunityView => {
    set({ opportunityView });
  },

  loadingTableOpportunities: null,
  setLoadingTableOpportunities: loading => {
    set({ loadingTableOpportunities: loading });
  },

  attributes: [],
  removeColumnByProperty: property_name => {
    set(state => ({
      attributes: state.attributes.filter(
        a => a.property_name !== property_name,
      ),
    }));
  },
  updateColumn: column => {
    set(state => {
      const attributes = [...state.attributes];
      const index = attributes.findIndex(
        a => a.property_name === column.property_name,
      );
      if (index !== -1) {
        attributes[index] = { ...attributes[index], ...column };
      }
      return { attributes };
    });
  },

  stages: [],
  setStages: stages => {
    set({ stages });
  },
  updateStage: (id, stage) => {
    if (!stage) return;

    set(state => {
      const stages = [...state.stages];
      const index = stages.findIndex(s => s.stage_id === id);
      if (index !== -1) {
        stages[index] = { ...stages[index], ...stage };
      }
      return { stages };
    });
  },
  deleteStage: id => {
    set(state => {
      const stage = state.stages.find(s => s.stage_id === id);
      if (!stage) return state;

      const updatedStages = state.stages.filter(s => s.stage_id !== id);
      const newOpportunities: Opportunity[] = state.opportunities.map(o =>
        o.stage_id !== id
          ? o
          : { ...o, stage_id: null, stage_name: "No Stage" },
      );
      const hasNoStage = updatedStages.some(s => s.stage_id === NO_STAGE_ID);
      const hasOpportunityWithNoStage = newOpportunities.some(o => !o.stage_id);

      if (!hasNoStage && hasOpportunityWithNoStage) {
        updatedStages.push({
          stage_id: NO_STAGE_ID,
          label: "No Stage",
          probability: 0,
          is_active: false,
          is_default: false,
          stage_order: updatedStages.length + 1,
        });
      }
      return {
        stages: updatedStages,
        opportunities: newOpportunities,
      };
    });
  },

  isSubmittedOpportunityForm: null,
  setIsSubmittedOpportunityForm: isSubmittedOpportunityForm =>
    set({ isSubmittedOpportunityForm }),

  originalOpportunities: [],
  setOriginalOpportunities: originalOpportunities =>
    set({ originalOpportunities }),

  fetchedPages: {},
  opportunities: [],
  opportunitiesByStageId: {},
  setOpportunities: opportunities => set({ opportunities }),
  addOpportunity: opportunity => {
    set(state => {
      const opportunitiesByStageId = { ...state.opportunitiesByStageId };
      if (opportunity.stage_id) {
        opportunitiesByStageId[opportunity.stage_id] = [
          ...(opportunitiesByStageId[opportunity.stage_id] || []),
          opportunity,
        ];
      }
      return {
        opportunitiesByStageId,
        opportunities: [...state.opportunities, opportunity],
      };
    });
  },
  removeOpportunity: id => {
    set(state => {
      const opportunitiesByStageId = { ...state.opportunitiesByStageId };
      const opportunity = state.opportunities.find(o => o.id === id);
      if (opportunity?.stage_id) {
        const stageOpportunities =
          opportunitiesByStageId[opportunity.stage_id] || [];
        opportunitiesByStageId[opportunity.stage_id] =
          stageOpportunities.filter(o => o.id !== opportunity.id);
      }
      return {
        opportunitiesByStageId,
        opportunities: state.opportunities.filter(o => o.id !== id),
      };
    });
  },
  // Only for table view
  removeOpportunities: (rowIds, gridApiRef) => {
    const gridRows = gridApiRef.current.getRowModels();
    rowIds.forEach(rowId => {
      gridRows.delete(rowId);
    });

    const entries = gridRows.entries();
    const updatedRow = Array.from(entries).map(([id, data]) => ({
      id: id as string,
      ...data,
    })) as Opportunity[];

    set({ opportunities: updatedRow });
  },
  updateOpportunity: (id, opportunity) => {
    set(state => {
      const opportunities = [...state.opportunities];
      const opportunitiesByStageId = { ...state.opportunitiesByStageId };

      const index = opportunities.findIndex(o => o.id === id);
      if (index !== -1) {
        opportunities[index] = { ...opportunities[index], ...opportunity };
      }

      const allOpportunities = Object.values(opportunitiesByStageId).flat();
      const indexInStageWiseOpps = allOpportunities.findIndex(o => o.id === id);
      const oldStageId = allOpportunities[indexInStageWiseOpps]?.stage_id;
      const newStageId = (opportunity.stage_id || oldStageId) as string;
      const hasStageIdChanged = newStageId !== oldStageId;

      if (indexInStageWiseOpps !== -1) {
        const updatedOpportunity = {
          ...allOpportunities[indexInStageWiseOpps],
          ...opportunity,
        };
        if (hasStageIdChanged && oldStageId) {
          const stageOpportunities = opportunitiesByStageId[oldStageId] || [];
          opportunitiesByStageId[oldStageId] = stageOpportunities.filter(
            o => o.id !== id,
          );
          opportunitiesByStageId[newStageId] = [
            ...(opportunitiesByStageId[newStageId] || []),
            updatedOpportunity,
          ];
        } else {
          const indexInCurrentStage =
            opportunitiesByStageId[newStageId]?.findIndex(o => o.id === id) ??
            -1;
          if (indexInCurrentStage !== -1) {
            opportunitiesByStageId[newStageId][indexInCurrentStage] =
              updatedOpportunity;
          } else {
            opportunitiesByStageId[newStageId] = [
              ...opportunitiesByStageId[newStageId],
              updatedOpportunity,
            ];
          }
        }
      }

      return { opportunities, opportunitiesByStageId };
    });
  },

  pagination: {
    limit: 50,
    page: 1,
    total: 0,
  },

  query: defaultOpportunityResponseQuery,
  setQuery: query => {
    set(state => ({
      query: {
        ...state.query,
        ...query,
      },
      fetchedPages: {},
    }));
  },

  filterViews: [],
  setFilterViews: filterViews => {
    set({ filterViews });
  },

  filters: [],
  setFilters: filters => {
    set({ filters, fetchedPages: {} });
  },

  activeFilterView: null,
  setActiveFilterView: view => {
    set({ activeFilterView: view });
  },
  dynamicValues: null!,
  setDynamicValues: dynamicValues => {
    set({ dynamicValues });
  },

  sortings: [],
  setSortings: sortings => {
    set({ sortings });
  },

  pipelinesWithViews: [],
  setPipelinesWithViews: pipelinesWithViews => {
    set({ pipelinesWithViews });
  },
  showablePipelinesWithViews: [],
  setShowablePipelinesWithViews: pipelinesWithViews => {
    set({ showablePipelinesWithViews: pipelinesWithViews });
  },

  pipelines: [],
  setPipelines: pipelines => {
    set({ pipelines });
  },
  activePipeline: null,
  setActivePipeline: pipeline => {
    if (pipeline?.pipeline_id) setActivePipelineInCookie(pipeline?.pipeline_id);
    set({ activePipeline: pipeline });
  },

  openNewOpportunityModal: false,
  setOpenNewOpportunityModal: val => {
    const { opportunities, stages } = get();

    set({
      openNewOpportunityModal: val,
      ...(val === false
        ? {
            newOpportunityFormInitialValues: {
              name: "",
              stage_id: stages.length > 0 ? stages[0].stage_id : "",
              deal_size: 0,
              close_date: dayjs() as any,
            },
          }
        : {
            newOpportunityFormInitialValues: {
              name: `Untitled Opportunities${
                opportunities.length > 0 ? ` ${opportunities.length + 1}` : ""
              }`,
              stage_id: stages.length > 0 ? stages[0].stage_id : "",
              deal_size: 0,
              close_date: dayjs() as any,
            },
          }),
    });
  },

  newOpportunityFormInitialValues: {
    name: "",
    stage_id: "",
    deal_size: 0,
    account_domain: null,
    close_date: dayjs() as any,
  },
  setNewOpportunityFormInitialValues: val => {
    const close_date = val.close_date ? dayjs(val.close_date) : dayjs();
    set({
      newOpportunityFormInitialValues: {
        ...val,
        close_date: close_date as unknown as string,
      },
    });
  },

  checkNoStageOpportunity: () => {
    const state = get();

    const opportunitiesWithNullStage = state.opportunities.filter(
      opportunity => !opportunity.stage_id,
    );
    const stages = [...state.stages];

    const hasNoStage = stages.some(s => s.stage_id === NO_STAGE_ID);

    if (opportunitiesWithNullStage.length > 0) {
      if (!hasNoStage) {
        stages.push({
          stage_id: NO_STAGE_ID,
          label: "No Stage",
          probability: 0,
          is_active: false,
          is_default: false,
          stage_order: stages.length + 1,
        });
      }
      set({ stages });
    } else {
      const newStages = stages.filter(s => s.stage_id !== NO_STAGE_ID);
      set({ stages: newStages });
    }
  },

  selectedRowIds: new Set(),
  setSelectedRowIds: rowIds => {
    set({ selectedRowIds: rowIds });
  },

  opportunityHighlights: [],
  addOpportunityHighlight: highlight => {
    set(state => ({
      opportunityHighlights: [...state.opportunityHighlights, highlight],
    }));
  },
  removeOpportunityHighlight: id => {
    set(state => ({
      opportunityHighlights: state.opportunityHighlights.filter(
        h => h.id !== id,
      ),
    }));
  },
  updateOpportunityHighlight: highlight => {
    set(state => {
      const opportunityHighlights = [...state.opportunityHighlights];
      const index = opportunityHighlights.findIndex(h => h.id === highlight.id);
      if (index !== -1) {
        opportunityHighlights[index] = highlight;
      }
      return { opportunityHighlights };
    });
  },

  rowSelectionModel: "",
  setRowSelectionModel: rowId => {
    set({ rowSelectionModel: rowId });
  },

  hoveredRowId: null,
  setHoveredRowId: rowId => {
    set({ hoveredRowId: rowId });
  },

  lastSelectedCell: null,
  setLastSelectedCell: lastSelectedCell => {
    set({ lastSelectedCell });
  },

  activeOpportunityData: null,
  setActiveOpportunityData: opportunity => {
    set({ activeOpportunityData: opportunity });
  },

  detailPanelContacts: [],
  setDetailPanelContacts: contacts => {
    set({ detailPanelContacts: contacts });
  },
  updateDetailsPanelContactById: (people_id, data) => {
    set(state => {
      const detailPanelContacts = [...state.detailPanelContacts];
      const index = detailPanelContacts.findIndex(
        c => c.people_id === people_id,
      );
      if (index !== -1) {
        detailPanelContacts[index] = { ...detailPanelContacts[index], ...data };
      }
      return { detailPanelContacts };
    });
  },

  detailPanelEmails: [],
  setDetailPanelEmails: email => {
    set({ detailPanelEmails: email });
  },

  detailPanelNotes: [],
  setDetailPanelNotes: notes => {
    set({ detailPanelNotes: notes });
  },

  activeDetailsPanelTab: "contacts",
  setActiveDetailsPanelTab: tab => {
    set({ activeDetailsPanelTab: tab });
  },

  newOpportunityIds: [],
  setNewOpportunityIds: ids => {
    set({ newOpportunityIds: ids });

    setTimeout(() => {
      set({ newOpportunityIds: [] });
    }, 1000);
  },

  stageWisePagination: {},
  setStageWisePagination: (stageId, pagination) => {
    set(state => ({
      stageWisePagination: {
        ...state.stageWisePagination,
        [stageId]: pagination,
      },
    }));
  },

  stageWiseLoading: {},
  setStageWiseLoading: (stageId, loading) => {
    set(state => ({
      stageWiseLoading: { ...state.stageWiseLoading, [stageId]: loading },
    }));
  },
});
